import { TuettuKieli } from '@lemon/core/model/common'

// Yksi paikka koko järjestelmän erottimelle, rivinvaihdolle ja BOM:ille.
// Vaihto tapahtuu päivittämällä nämä vakiot.
export const CSV_SEPARATOR = ';'
export const CSV_NEWLINE = '\r\n'
export const CSV_UTF8_BOM = '\uFEFF'

// Käsittelee yhden solun arvon CSV:n lainausmerkkisääntöjen mukaan:
//   1. Arvon sisäiset lainausmerkit kahdennetaan — tämä on aina pakollista,
//      koska lainausmerkki on CSV:n erityismerkki
//   2. Itse arvo lainataan, jos siinä on jokin merkki joka muuten rikkoisi
//      rakenteen: lainausmerkki, rivinvaihto (CR tai LF) tai erotinmerkki
// Esimerkkejä (separator = ';'):
//   'asiakas'         → asiakas              (ei lainata, ei erityismerkkejä)
//   'Smith;Jones'     → "Smith;Jones"        (erotin solun sisällä → lainataan)
//   'hän sanoi "moi"' → "hän sanoi ""moi"""  (lainausmerkit kahdennetaan + lainataan)
//   'rivi1\nrivi2'    → "rivi1\nrivi2"       (rivinvaihto solun sisällä → lainataan)
export const escapeCsvValue = (value: string, separator: string = CSV_SEPARATOR): string => {
  if (value === null || value === undefined) {
    return ''
  }
  // 1) Kahdennetaan kaikki sisäiset lainausmerkit
  const stringValue = String(value).replace(/"/g, '""')

  // 2) Lainataan koko arvo jos se sisältää lainausmerkin, rivinvaihdon (CR tai LF) tai erottimen.
  //    Erotin upotetaan suoraan regexiin — oletetaan että se on ';' tai ',' (ei regex-erityismerkki).
  const escapeRegex = new RegExp('["\\r\\n' + separator + ']')
  if (escapeRegex.test(stringValue)) {
    return `"${stringValue}"`
  }
  return stringValue
}

/** Solun sisällön tyyppi, lisää tarvittaessa käsittelyjä muille tyypeille */
export type SpreadsheetCellValue = string | number // | boolean | Date | LocalDate | LocalDateTime | Timestamp | null | undefined

export interface SpreadsheetBuilderOptions {
  separator?: string
  newline?: string
  withBom?: boolean
  // Vaikuttaa päivämäärien ja desimaalien muotoiluun. Oletus 'fi'.
  kieli?: TuettuKieli
}

const DEFAULT_WITH_BOM = true

interface FormatContext {
  kieli: TuettuKieli

  // Tarvittaessa lisää tähän vapaaehtoisia palveluita (esim. DateService,
  // CurrencyService) jos solujen formatoinneille tarvitaan yleistä logiikkaa
  // Tällä hetkellä formatointi tehdään kutsujan toimesta.
  // dateService?: DateService
  // currencyService?: CurrencyService
}

// Sisäinen rivien tallennusmuoto: data säilytetään natiivityypeissä,
// formatointi tapahtuu vasta toString()-vaiheessa. Tämä mahdollistaa
// tulevaisuudessa toivottavasti muiden esitysmuotojen (esim. Excel) tuominen pienemmällä työllä
type StoredRow = { kind: 'data'; cells: SpreadsheetCellValue[] } | { kind: 'header'; cells: SpreadsheetCellValue[] } | { kind: 'empty' } | { kind: 'raw'; line: string }

export class SpreadsheetBuilder {
  private readonly _separator: string
  private readonly _newline: string
  private readonly _withBom: boolean
  private readonly _ctx: FormatContext
  private readonly _rows: StoredRow[] = []

  constructor(options?: SpreadsheetBuilderOptions) {
    this._separator = options?.separator ?? CSV_SEPARATOR
    this._newline = options?.newline ?? CSV_NEWLINE
    this._withBom = options?.withBom ?? DEFAULT_WITH_BOM
    this._ctx = {
      kieli: options?.kieli ?? 'fi',
    }
  }

  get separator(): string {
    return this._separator
  }
  get newline(): string {
    return this._newline
  }

  /**
   * Otsikkorivin lisäys. CSV:ssä identtinen datariviltä, mutta valmistelee muita formaatteja varten
   */
  addHeader(values: SpreadsheetCellValue[]): void {
    this._rows.push({ kind: 'header', cells: [...values] })
  }

  /**
   * Datarivin lisäys
   */
  addRow(values: SpreadsheetCellValue[]): void {
    this._rows.push({ kind: 'data', cells: [...values] })
  }

  addRows(rows: SpreadsheetCellValue[][]): void {
    for (const row of rows) {
      this.addRow(row)
    }
  }

  // Tyhjä rivi (ei soluja, ei erottimia) — esim. raportin osioiden välissä
  addEmptyRow(): void {
    this._rows.push({ kind: 'empty' })
  }

  /**
   * Raakarivin lisäys ilman escape-käsittelyä — käyttöön vain kun rivi
   * on koottu jo erikseen ja sen sisältö on tiedossa turvalliseksi
   */
  addRawLine(line: string): void {
    this._rows.push({ kind: 'raw', line })
  }

  toString(): string {
    if (this._rows.length === 0) {
      return this._withBom ? CSV_UTF8_BOM : ''
    }
    const lines = this._rows.map(r => this._rowToCsvLine(r))
    const body = lines.join(this._newline) + this._newline
    return this._withBom ? CSV_UTF8_BOM + body : body
  }

  private _rowToCsvLine(row: StoredRow): string {
    switch (row.kind) {
      case 'empty':
        return ''
      case 'raw':
        return row.line
      case 'header':
      case 'data': {
        const escaped = row.cells.map(v => escapeCsvValue(_toCellString(v, this._ctx), this._separator))
        return escaped.join(this._separator)
      }
    }
  }
}

const _toCellString = (value: SpreadsheetCellValue, ctx: FormatContext): string => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  if (typeof value === 'number') {
    return String(value)
  }

  /** Lisää tarvittaessa käsittelyjä muille SpreadsheetCellValue tyypeille */
  // if (isNumberDate()) {
  //   ...
  // }

  return String(value)
}

/**
 * Tyypitetty builder, joka hyödyntää SpreadsheetBuilderia ja tarjoaa tyypitetyn rajapinnan solujen määrittelyyn.
 */
export interface SpreadsheetSarake<T> {
  header: string
  value: (row: T) => SpreadsheetCellValue
}

export class TypedSpreadsheetBuilder<T> {
  private readonly _builder: SpreadsheetBuilder
  private readonly _columns: readonly SpreadsheetSarake<T>[]
  private readonly _ctx: FormatContext

  constructor(columns: SpreadsheetSarake<T>[], options?: SpreadsheetBuilderOptions) {
    this._builder = new SpreadsheetBuilder(options)
    this._columns = columns
    this._ctx = {
      kieli: options?.kieli ?? 'fi',
    }

    this._builder.addHeader(columns.map(c => c.header))
  }

  addRow(row: T) {
    const cells = this._columns.map(sarake => this._formatCell(sarake, sarake.value(row)))
    this._builder.addRow(cells)
  }

  addRows(rows: T[]) {
    for (const row of rows) {
      this.addRow(row)
    }
  }

  toString(): string {
    return this._builder.toString()
  }

  private _formatCell(_: SpreadsheetSarake<T>, raw: SpreadsheetCellValue): string {
    return _toCellString(raw, this._ctx)
  }
}
